import {
  useZakekeHelpers,
  useZakekeProduct,
  useZakekeProductOptions,
} from "@zakeke/zakeke-customizer-react";
import { useAppStore, useSetItems, useGetItems } from "../state/store";
import { Map } from "immutable";
import { SideOptions } from "../state/interfaces/sideOptions";
import { mapLibraryItemsToUIItems } from "./common";
import usePrintMethods from "./usePrintMethods";
import useWaitUntilItemRerenderStops from './useWaitUntilItemRerenderStops';
import { isEqual } from "lodash";

type ChangeVariantOptions = {
  preventResetEnabled?: boolean;
  preventAdapt?: boolean;
  preventApplyPrintMethods?: boolean;
};
const useVariantChanger = () => {
  const product = useZakekeProduct();
  const productOptions = useZakekeProductOptions();
  const { getAdaptItems, getImage } = useZakekeHelpers();
  const waitUntilItemsRerenderStops = useWaitUntilItemRerenderStops();
  const setItems = useSetItems();
  const getItems = useGetItems();
  const { adaptSideToPrintMethod } = usePrintMethods();

  return async (variantId: number, options?: ChangeVariantOptions) => {
    const {
      selectedSideId,
      sidesPrintMethods,
      resizableOptions,
      currentTemplate,
      selectedVariantId: oldVariantId,
			sideAreaBackgrounds,
    } = useAppStore.getState();

    const oldVariant = product.variants.find((x) => x.id === oldVariantId);
    const selectedVariant = product.variants.find((x) => x.id === variantId);

    if (!selectedVariant) throw new Error(`Invalid variant id ${variantId}`);

    const getSideId = () => {
      if (selectedVariant.sides.some((x) => x.id === selectedSideId)) {
        return selectedSideId;
      }
      return selectedVariant.sides[0].id;
    };

    const getSides = () => {
      if (oldVariant) {
        return selectedVariant.sides
          .filter((newSide) => {
            const oldAreas = oldVariant.sides.find(
              (s) => s.id === newSide.id
            )?.areas;
            // avvio ricalcolo se numero di aree è diverso o se le stesse aree hanno grandezze diverse
            const sameArea = newSide.areas.find((newSideArea) => {
              return !oldAreas?.find((oldSideArea) => {
                return (
                  oldSideArea.id === newSideArea.id &&
                  oldSideArea.width === newSideArea.width &&
                  oldSideArea.height === newSideArea.height
                );
              });
            });
            return (
              !oldAreas || oldAreas.length !== newSide.areas.length || sameArea
            );
          })
          .map((i) => i.id);
      }
      return [];
    };

    const getSidesPrintMethods = () => {
      return (
        selectedVariant.sides.reduce((reviewedPrintTypes, side) => {
          const printIdForSide = reviewedPrintTypes.get(side.id);
          if (
            !(
              printIdForSide &&
              side.printTypes.some((x) => x.id === printIdForSide)
            )
          ) {
            // search if side has a print type compatible with the product
            // it could have an array of sideprint types where only one is still assigned to the product
            const matchingPrintType = side.printTypes.find((productPrintType) =>
              product.printTypes.some(
                (sidePrintType) => sidePrintType.id === productPrintType.id
              )
            );
            return reviewedPrintTypes.set(
              side.id,
              matchingPrintType?.id ?? product.printTypes[0].id
            );
          }
          return reviewedPrintTypes;
        }, sidesPrintMethods) ??
        Map(
          selectedVariant.sides.map((x) => {
            return [x.id, product.printTypes[0].id];
          })
        )
      );
    };

    const getSidesOptions = () => {
      if (resizableOptions.size === 0) {
        return Map(
          productOptions.map((x) => {
            let sidesOptions = Map<number, SideOptions>();
            if (x.options === null && x.sides) {
              // Side options
              sidesOptions = Map(
                x.sides
                  .filter((x) => x.options)
                  .map((y) => {
                    return [
                      y.sideId,
                      {
                        width: y.options?.isResizable
                          ? y.options!.startWidth
                          : null,
                        height: y.options?.isResizable
                          ? y.options!.startHeight
                          : null,
                        columns: y.options?.startSplitColumns ?? 1,
                        rows: y.options?.startSplitRows ?? 1,
                      },
                    ];
                  })
              );
            } else if (x.options) {
              const variant = product.variants.find(
                (y) => y.id === selectedVariant.id
              )!;

              // Variant options
              sidesOptions = Map(
                variant.sides.map((y) => {
                  return [
                    y.id,
                    {
                      width: x.options?.isResizable
                        ? x.options!.startWidth
                        : null,
                      height: x.options?.isResizable
                        ? x.options!.startHeight
                        : null,
                      columns: x.options?.startSplitColumns ?? 1,
                      rows: x.options?.startSplitRows ?? 1,
                    },
                  ];
                })
              );
            }

            return [x.variantId, sidesOptions];
          })
        );
      }
      return resizableOptions;
    };

    if (!options?.preventApplyPrintMethods) {
			const newPrintMethods = getSidesPrintMethods();
			const oldPrintMethods = useAppStore.getState().sidesPrintMethods;
      const oldItems = getItems();
      const adapted = await [
        ...oldPrintMethods.entries(),
      ].reduce(
        async (accPromise, [sideId, printTypeId]) => {
					const newPrintTypeId = newPrintMethods.get(sideId);
          if ( !newPrintTypeId || newPrintTypeId === printTypeId ) {
            return accPromise;
          }
          const acc = await accPromise;
          return adaptSideToPrintMethod(
            product,
            acc.sidePrintMethods,
            acc.sideAreaBackgrounds,
            variantId,
            acc.items,
            sideId,
            newPrintTypeId
          );
        },
        Promise.resolve({
          items: oldItems,
          sideAreaBackgrounds,
          sidePrintMethods: oldPrintMethods,
        })
      );
      if (!isEqual(adapted.items, oldItems)) {
        const { items } = adapted;
        setItems(() => items, true);
        await waitUntilItemsRerenderStops(items.map((i) => i.guid));
      }
    }

    useAppStore.setState({
      selectedVariantId: variantId,
      selectedSideId: getSideId(),
      forceResetEnabled: !options?.preventResetEnabled,
      sidesPrintMethods: getSidesPrintMethods(),
      resizableOptions: getSidesOptions(),
    });

    if (!options?.preventAdapt) {
      const sides = getSides();
      const newItems = await getAdaptItems(
        variantId,
        oldVariantId!,
        currentTemplate?.adaptItemsToVariants ?? 0
      );
      if (newItems) {
        const oldItems = getItems();
        const items = await mapLibraryItemsToUIItems(newItems, getImage, i => {
          i.isFromTemplate = oldItems.find(oldItem => oldItem.guid === i.guid)?.isFromTemplate ?? false;
          return Promise.resolve(i);
        });
        if (items && items.length > 0 && sides.length) {
          setItems((prev) => {
            return prev.filter((i) => !sides.includes(i.sideId)).concat(items);
          });
        }
      }
    }
  };
};

export default useVariantChanger;
